/**
 * Shared Mongo expression helpers and payroll_list matchers used by
 * every payroll-report pipeline module.
 */

export const toNumber = (field) => ({
  $convert: { input: field, to: "double", onError: 0, onNull: 0 },
});

export const sumNumberOrArray = (field) => ({
  $cond: {
    if: { $isArray: field },
    then: {
      $sum: {
        $map: {
          input: field,
          as: "amount",
          in: toNumber("$$amount"),
        },
      },
    },
    else: toNumber(field),
  },
});

/**
 * payroll.payrollID === payroll_list.id/_id.
 * Only Printed payroll runs count toward report totals.
 */
const IS_BACK_PAY = {
  $or: [
    {
      $and: [
        { $eq: ["$payrollListDoc.payrollType", "Basic Pay"] },
        { $eq: ["$payrollListDoc.jobType", "Job Order"] },
        { $eq: ["$payrollListDoc.payItem", "Back Pay"] },
      ],
    },
    {
      $and: [
        { $eq: ["$payrollListDoc.payrollType", "Basic Pay"] },
        { $eq: ["$payrollListDoc.payItem", "Addendum Back Pay"] },
      ],
    },
    {
      $and: [
        { $eq: ["$payrollListDoc.payrollType", "Augmentation Pay"] },
        { $eq: ["$payrollListDoc.payItem", "Back Pay"] },
      ],
    },
    {
      $and: [
        { $eq: ["$payrollListDoc.payrollType", "Individual Payroll"] },
        {
          $in: [
            "$payrollListDoc.payItem",
            ["Initial Salary(AP)", "Last Salary(AP)"],
          ],
        },
      ],
    },
    {
      $and: [
        { $eq: ["$payrollListDoc.payrollType", "Individual Payroll"] },
        { $eq: ["$payrollListDoc.jobType", "Job Order"] },
        { $eq: ["$payrollListDoc.payItem", "Last Salary"] },
      ],
    },
  ],
};

export function matchPrintedPayrollList(isBackPay) {
  return [
    {
      $lookup: {
        from: "payroll_list",
        localField: "payrollID",
        foreignField: "_id",
        pipeline: [{ $match: { stats: "Printed" } }],
        as: "payrollListDoc",
      },
    },
    { $unwind: "$payrollListDoc" },
    { $match: { $expr: isBackPay ? IS_BACK_PAY : { $not: [IS_BACK_PAY] } } },
  ];
}

/** General Payroll report: Printed Basic Pay runs with payItem "General Payroll". */
export function matchPrintedGeneralPayrollList() {
  return [
    {
      $lookup: {
        from: "payroll_list",
        localField: "payrollID",
        foreignField: "_id",
        pipeline: [
          {
            $match: {
              stats: "Printed",
              payrollType: "Basic Pay",
              payItem: "General Payroll",
            },
          },
        ],
        as: "payrollListDoc",
      },
    },
    { $unwind: "$payrollListDoc" },
  ];
}

/** Parses the key and amount from a stored "name: amount" array entry. */
export const ENTRY_KEY = {
  $trim: {
    input: {
      $arrayElemAt: [{ $split: ["$$this", ":"] }, 0],
    },
  },
};

/**
 * Contributions/back-pay entries can be period lists ("100,200").
 * Matches payslip parsing: split on commas and sum each part.
 */
export const ENTRY_AMOUNT_PERIOD_SUM = {
  $sum: {
    $map: {
      input: {
        $split: [
          {
            $trim: {
              input: {
                $arrayElemAt: [{ $split: ["$$this", ":"] }, 1],
              },
            },
          },
          ",",
        ],
      },
      as: "part",
      in: toNumber({ $trim: { input: "$$part" } }),
    },
  },
};

export function buildNamedDeductionSum(keys) {
  return {
    $reduce: {
      input: { $ifNull: ["$deductions", []] },
      initialValue: 0,
      in: {
        $add: [
          "$$value",
          {
            $cond: {
              if: { $in: [ENTRY_KEY, keys] },
              then: ENTRY_AMOUNT_PERIOD_SUM,
              else: 0,
            },
          },
        ],
      },
    },
  };
}

export function buildNamedContributionSum(keys) {
  return {
    $reduce: {
      input: { $ifNull: ["$contributions", []] },
      initialValue: 0,
      in: {
        $add: [
          "$$value",
          {
            $cond: {
              if: { $in: [ENTRY_KEY, keys] },
              then: ENTRY_AMOUNT_PERIOD_SUM,
              else: 0,
            },
          },
        ],
      },
    },
  };
}

export function buildEntryArraySum(field, excludedKeys = []) {
  const normalizedExcludedKeys = excludedKeys.map((key) => key.toLowerCase());

  return {
    $reduce: {
      input: { $ifNull: [field, []] },
      initialValue: 0,
      in: {
        $add: [
          "$$value",
          {
            $cond: {
              if: {
                $not: [
                  {
                    $in: [
                      { $toLower: ENTRY_KEY },
                      normalizedExcludedKeys,
                    ],
                  },
                ],
              },
              then: ENTRY_AMOUNT_PERIOD_SUM,
              else: 0,
            },
          },
        ],
      },
    },
  };
}

export function buildSumGroup(id, sums) {
  return Object.fromEntries([
    ["_id", id],
    ...Object.entries(sums).map(([key, expression]) => [
      key,
      { $sum: expression },
    ]),
  ]);
}

export function buildEmployeeAmountPipeline(employeeIds, valueKey, sumExpr) {
  return [
    { $match: { "employee.id": { $in: employeeIds } } },
    ...matchPrintedPayrollList(false),
    {
      $unionWith: {
        coll: "payroll_back_pay",
        pipeline: [
          { $match: { "employee.id": { $in: employeeIds } } },
          ...matchPrintedPayrollList(true),
        ],
      },
    },
    {
      $group: {
        _id: "$employee.id",
        [valueKey]: { $sum: sumExpr },
      },
    },
    { $project: { _id: 0, employeeId: "$_id", [valueKey]: 1 } },
  ];
}

/** Empty employeeIds means "every employee" — used by export to run one pass. */
export function buildEmployeeSummaryPipeline(employeeIds, sums, rowStages = []) {
  const matchEmployees =
    employeeIds.length > 0
      ? [{ $match: { "employee.id": { $in: employeeIds } } }]
      : [];

  return [
    ...matchEmployees,
    ...rowStages,
    ...matchPrintedPayrollList(false),
    {
      $unionWith: {
        coll: "payroll_back_pay",
        pipeline: [
          ...matchEmployees,
          ...rowStages,
          ...matchPrintedPayrollList(true),
        ],
      },
    },
    { $group: buildSumGroup("$employee.id", sums) },
    {
      $project: {
        _id: 0,
        employeeId: "$_id",
        ...Object.fromEntries(Object.keys(sums).map((key) => [key, 1])),
      },
    },
  ];
}

export function buildPayrollAmountLookups(prefix, valueKey, sumExpr) {
  return [
    {
      $lookup: {
        from: "payroll",
        let: { employeeId: "$_id" },
        pipeline: [
          { $match: { $expr: { $eq: ["$employee.id", "$$employeeId"] } } },
          ...matchPrintedPayrollList(false),
          {
            $group: {
              _id: null,
              [valueKey]: { $sum: sumExpr },
            },
          },
        ],
        as: `${prefix}RegularLookup`,
      },
    },
    {
      $lookup: {
        from: "payroll_back_pay",
        let: { employeeId: "$_id" },
        pipeline: [
          { $match: { $expr: { $eq: ["$employee.id", "$$employeeId"] } } },
          ...matchPrintedPayrollList(true),
          {
            $group: {
              _id: null,
              [valueKey]: { $sum: sumExpr },
            },
          },
        ],
        as: `${prefix}BackPayLookup`,
      },
    },
  ];
}

/** Sum of regular + back-pay lookup docs for a single amount field. */
export function sumRegularAndBackPay(prefix, valueKey) {
  return {
    $add: [
      { $ifNull: [`$${prefix}RegularDoc.${valueKey}`, 0] },
      { $ifNull: [`$${prefix}BackPayDoc.${valueKey}`, 0] },
    ],
  };
}

export function attachRegularAndBackPayDocs(addFields, prefix) {
  addFields[`${prefix}RegularDoc`] = {
    $arrayElemAt: [`$${prefix}RegularLookup`, 0],
  };
  addFields[`${prefix}BackPayDoc`] = {
    $arrayElemAt: [`$${prefix}BackPayLookup`, 0],
  };
}
